// Header.js
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Mail, MapPin, Phone, ChevronDown } from "lucide-react";
import logo from "../assets/logo.png";
import chatIcon from "../assets/chat.png";
import { AUSTRALIA_CARE_WORKFORCE_APPLICATIONS_OPEN } from "../constants";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPRDropdownOpen, setIsPRDropdownOpen] = useState(false);
  const [isMobilePROpen, setIsMobilePROpen] = useState(false);

  return (
    <header className="w-full bg-white relative z-50">
      {/* Top Bar */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-2 flex flex-col md:flex-row justify-between items-center text-xs text-gray-500 font-light">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Mail size={14} />
              <span>info@notapaths.com</span>
            </div>
            <div className="hidden md:flex items-center gap-2">
              <Phone size={14} />
              <span>07418 622985</span>
            </div>
          </div>
          <div className="flex items-center gap-4 mt-2 md:mt-0">
            <Link to="/contact" className="hover:text-[#202356] transition">
              Book Consultation
            </Link>
            <Link to="/contact" className="hover:text-[#202356] transition">
              Contact Us
            </Link>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img src={logo} alt="NOTAPATHS Logo" className="h-10" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center space-x-8 text-gray-700 font-normal">
          <Link to="/study-abroad" className="hover:text-[#202356] transition">
            Study Abroad
          </Link>
          <Link to="/work-abroad" className="hover:text-[#202356] transition">
            Work Abroad
          </Link>

          {/* PR Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setIsPRDropdownOpen(true)}
            onMouseLeave={() => setIsPRDropdownOpen(false)}
          >
            <button className="flex items-center gap-1 hover:text-[#202356] transition py-2">
              Permanent Residency
              <ChevronDown
                size={16}
                className={`transition-transform ${isPRDropdownOpen ? "rotate-180" : ""}`}
              />
            </button>

            {isPRDropdownOpen && (
              <div className="absolute top-full left-0 pt-2 w-56 z-50">
                <div className="bg-white shadow-lg rounded-lg border border-gray-200 py-2">
                  <Link
                    to="/permanent-residency/canada"
                    className="block px-4 py-2 hover:bg-gray-50 transition text-sm"
                  >
                    🇨🇦 Canada
                  </Link>
                  <Link
                    to="/permanent-residency/australia"
                    className="block px-4 py-2 hover:bg-gray-50 transition text-sm"
                  >
                    🇦🇺 Australia
                  </Link>
                  <Link
                    to="/permanent-residency/new-zealand"
                    className="block px-4 py-2 hover:bg-gray-50 transition text-sm"
                  >
                    <span>🇳🇿</span> New Zealand
                  </Link>
                  <Link
                    to="/permanent-residency/portugal"
                    className="block px-4 py-2 hover:bg-gray-50 transition text-sm"
                  >
                    <span>🇵🇹</span> Portugal
                  </Link>
                  <Link
                    to="/permanent-residency/australia-care-workforce"
                    className="flex items-center justify-between gap-3 px-4 py-2 hover:bg-gray-50 transition text-sm"
                  >
                    <span>🇦🇺 AU Care Workforce</span>
                    <span
                      className={`inline-flex items-center rounded-full text-[10px] font-medium px-2 py-1 ${
                        AUSTRALIA_CARE_WORKFORCE_APPLICATIONS_OPEN
                          ? "bg-[#EAF0FF] text-[#2542D4]"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {AUSTRALIA_CARE_WORKFORCE_APPLICATIONS_OPEN
                        ? "New"
                        : "Closed"}
                    </span>
                  </Link>
                </div>
              </div>
            )}
          </div>

          <Link to="/pricing" className="hover:text-[#202356] transition">
            Pricing
          </Link>
          <Link to="/about" className="hover:text-[#202356] transition">
            About Us
          </Link>
          <Link to="/contact" className="hover:text-[#202356] transition">
            Contact
          </Link>
        </nav>

        {/* Login Button */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://app.notapaths.com"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#202356] text-white px-6 py-2 rounded-lg hover:bg-[#2a2f7a] transition"
          >
            Login
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button className="md:hidden" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Nav */}
      {isOpen && (
        <div className="absolute top-full left-0 w-full bg-white shadow-lg md:hidden z-40 border-t border-gray-200">
          <nav className="flex flex-col py-4">
            <Link
              to="/study-abroad"
              className="px-6 py-3 hover:bg-gray-50 transition"
              onClick={() => setIsOpen(false)}
            >
              Study Abroad
            </Link>
            <Link
              to="/work-abroad"
              className="px-6 py-3 hover:bg-gray-50 transition"
              onClick={() => setIsOpen(false)}
            >
              Work Abroad
            </Link>

            {/* Mobile PR Dropdown */}
            <div>
              <button
                onClick={() => setIsMobilePROpen(!isMobilePROpen)}
                className="w-full px-6 py-3 hover:bg-gray-50 transition flex items-center justify-between"
              >
                <span>Permanent Residency</span>
                <ChevronDown
                  size={16}
                  className={`transition-transform ${isMobilePROpen ? "rotate-180" : ""}`}
                />
              </button>

              {isMobilePROpen && (
                <div className="bg-gray-50">
                  <Link
                    to="/permanent-residency/canada"
                    className="block px-10 py-2 hover:bg-gray-100 transition text-sm"
                    onClick={() => setIsOpen(false)}
                  >
                    🇨🇦 Canada
                  </Link>
                  <Link
                    to="/permanent-residency/australia"
                    className="block px-10 py-2 hover:bg-gray-100 transition text-sm"
                    onClick={() => setIsOpen(false)}
                  >
                    🇦🇺 Australia
                  </Link>
                  <Link
                    to="/permanent-residency/new-zealand"
                    className="block px-10 py-2 hover:bg-gray-100 transition text-sm"
                    onClick={() => setIsOpen(false)}
                  >
                    <span>🇳🇿</span> New Zealand
                  </Link>
                  <Link
                    to="/permanent-residency/portugal"
                    className="block px-10 py-2 hover:bg-gray-100 transition text-sm"
                    onClick={() => setIsOpen(false)}
                  >
                    <span>🇵🇹</span> Portugal
                  </Link>
                  <Link
                    to="/permanent-residency/australia-care-workforce"
                    className="flex items-center justify-between gap-3 px-10 py-2 hover:bg-gray-100 transition text-sm"
                    onClick={() => setIsOpen(false)}
                  >
                    <span>🇦🇺 AU Care Workforce</span>
                    <span
                      className={`inline-flex items-center rounded-full text-[10px] font-medium px-2 py-1 ${
                        AUSTRALIA_CARE_WORKFORCE_APPLICATIONS_OPEN
                          ? "bg-[#EAF0FF] text-[#2542D4]"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {AUSTRALIA_CARE_WORKFORCE_APPLICATIONS_OPEN
                        ? "New"
                        : "Closed"}
                    </span>
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/pricing"
              className="px-6 py-3 hover:bg-gray-50 transition"
              onClick={() => setIsOpen(false)}
            >
              Pricing
            </Link>
            <Link
              to="/about"
              className="px-6 py-3 hover:bg-gray-50 transition"
              onClick={() => setIsOpen(false)}
            >
              About Us
            </Link>
            <Link
              to="/contact"
              className="px-6 py-3 hover:bg-gray-50 transition"
              onClick={() => setIsOpen(false)}
            >
              Contact
            </Link>
            <div className="px-6 py-3 border-t border-gray-200 mt-2">
              <a
                href="https://app.notapaths.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-[#202356] text-white px-6 py-2 rounded-lg hover:bg-[#2a2f7a] transition inline-block text-center"
              >
                Login
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;

// // Header.js
// import React, { useState } from 'react';
// import { Link } from 'react-router-dom';
// import { Menu, X, Mail, MapPin, Phone, ChevronDown } from 'lucide-react';
// import logo from '../assets/logo.png';
// import chatIcon from '../assets/chat.png';

// const Header = () => {
//   const [isOpen, setIsOpen] = useState(false);
//   const [isPRDropdownOpen, setIsPRDropdownOpen] = useState(false);
//   const [isMobilePROpen, setIsMobilePROpen] = useState(false);

//   return (
//     <header className="w-full bg-white relative z-50">
//       {/* Top Bar */}
//       <div className="bg-gray-50 border-b border-gray-100">
//         <div className="max-w-7xl mx-auto px-4 md:px-8 py-2 flex flex-col md:flex-row justify-between items-center text-xs text-gray-500 font-light">
//           <div className="flex items-center gap-6">
//             <div className="flex items-center gap-2">
//               <Mail size={14} />
//               <span>info@notapaths.com</span>
//             </div>
//             <div className="hidden md:flex items-center gap-2">
//               <Phone size={14} />
//               <span>07418 622985</span>
//             </div>
//           </div>
//           <div className="flex items-center gap-4 mt-2 md:mt-0">
//             <Link to="/contact" className="hover:text-[#202356] transition">
//               Book Consultation
//             </Link>
//             <Link to="/contact" className="hover:text-[#202356] transition">
//               Contact Us
//             </Link>
//           </div>
//         </div>
//       </div>

//       {/* Main Navigation */}
//       <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
//         {/* Logo */}
//         <Link to="/" className="flex items-center">
//           <img src={logo} alt="NOTAPATHS Logo" className="h-10" />
//         </Link>

//         {/* Desktop Nav */}
//         <nav className="hidden md:flex items-center space-x-8 text-gray-700 font-normal">
//           <Link to="/study-abroad" className="hover:text-[#202356] transition">
//             Study Abroad
//           </Link>
//           <Link to="/work-abroad" className="hover:text-[#202356] transition">
//             Work Abroad
//           </Link>

//           {/* PR Dropdown */}
//           <div
//             className="relative"
//             onMouseEnter={() => setIsPRDropdownOpen(true)}
//             onMouseLeave={() => setIsPRDropdownOpen(false)}
//           >
//             <button className="flex items-center gap-1 hover:text-[#202356] transition py-2">
//               Permanent Residency
//               <ChevronDown size={16} className={`transition-transform ${isPRDropdownOpen ? 'rotate-180' : ''}`} />
//             </button>

//             {isPRDropdownOpen && (
//               <div className="absolute top-full left-0 pt-2 w-56 z-50">
//                 <div className="bg-white shadow-lg rounded-lg border border-gray-200 py-2">
//                   <Link
//                     to="/permanent-residency/canada"
//                     className="block px-4 py-2 hover:bg-gray-50 transition text-sm"
//                   >
//                     🇨🇦 Canada
//                   </Link>
//                   <Link
//                     to="/permanent-residency/australia"
//                     className="block px-4 py-2 hover:bg-gray-50 transition text-sm"
//                   >
//                     🇦🇺 Australia
//                   </Link>
//                   <Link
//                     to="/permanent-residency/new-zealand"
//                     className="block px-4 py-2 hover:bg-gray-50 transition text-sm"
//                   >
//                     <span>🇳🇿</span> New Zealand
//                   </Link>
//                   <Link
//                     to="/permanent-residency/portugal"
//                     className="block px-4 py-2 hover:bg-gray-50 transition text-sm"
//                   >
//                     <span>🇵🇹</span> Portugal
//                   </Link>
//                   <Link
//                     to="/permanent-residency/australia-care-workforce"
//                     className="flex items-center justify-between gap-3 px-4 py-2 hover:bg-gray-50 transition text-sm"
//                   >
//                     <span>🇦🇺 AU Care Workforce</span>
//                     <span className="inline-flex items-center rounded-full bg-[#EAF0FF] text-[#2542D4] text-[10px] font-medium px-2 py-1">
//                       New
//                     </span>
//                   </Link>
//                 </div>
//               </div>
//             )}
//           </div>

//           <Link to="/pricing" className="hover:text-[#202356] transition">
//             Pricing
//           </Link>
//           <Link to="/about" className="hover:text-[#202356] transition">
//             About Us
//           </Link>
//           <Link to="/contact" className="hover:text-[#202356] transition">
//             Contact
//           </Link>
//         </nav>

//         {/* Login Button */}
//         <div className="hidden md:flex items-center gap-3">
//           <a
//             href="https://app.notapaths.com"
//             target="_blank"
//             rel="noopener noreferrer"
//             className="bg-[#202356] text-white px-6 py-2 rounded-lg hover:bg-[#2a2f7a] transition"
//           >
//             Login
//           </a>
//         </div>

//         {/* Mobile Menu Button */}
//         <button className="md:hidden" onClick={() => setIsOpen(!isOpen)}>
//           {isOpen ? <X size={28} /> : <Menu size={28} />}
//         </button>
//       </div>

//       {/* Mobile Nav */}
//       {isOpen && (
//         <div className="absolute top-full left-0 w-full bg-white shadow-lg md:hidden z-40 border-t border-gray-200">
//           <nav className="flex flex-col py-4">
//             <Link
//               to="/study-abroad"
//               className="px-6 py-3 hover:bg-gray-50 transition"
//               onClick={() => setIsOpen(false)}
//             >
//               Study Abroad
//             </Link>
//             <Link
//               to="/work-abroad"
//               className="px-6 py-3 hover:bg-gray-50 transition"
//               onClick={() => setIsOpen(false)}
//             >
//               Work Abroad
//             </Link>

//             {/* Mobile PR Dropdown */}
//             <div>
//               <button
//                 onClick={() => setIsMobilePROpen(!isMobilePROpen)}
//                 className="w-full px-6 py-3 hover:bg-gray-50 transition flex items-center justify-between"
//               >
//                 <span>Permanent Residency</span>
//                 <ChevronDown size={16} className={`transition-transform ${isMobilePROpen ? 'rotate-180' : ''}`} />
//               </button>

//               {isMobilePROpen && (
//                 <div className="bg-gray-50">
//                   <Link
//                     to="/permanent-residency/canada"
//                     className="block px-10 py-2 hover:bg-gray-100 transition text-sm"
//                     onClick={() => setIsOpen(false)}
//                   >
//                     🇨🇦 Canada
//                   </Link>
//                   <Link
//                     to="/permanent-residency/australia"
//                     className="block px-10 py-2 hover:bg-gray-100 transition text-sm"
//                     onClick={() => setIsOpen(false)}
//                   >
//                     🇦🇺 Australia
//                   </Link>
//                   <Link
//                     to="/permanent-residency/new-zealand"
//                     className="block px-10 py-2 hover:bg-gray-100 transition text-sm"
//                     onClick={() => setIsOpen(false)}
//                   >
//                     <span>🇳🇿</span> New Zealand
//                   </Link>
//                   <Link
//                     to="/permanent-residency/portugal"
//                     className="block px-10 py-2 hover:bg-gray-100 transition text-sm"
//                     onClick={() => setIsOpen(false)}
//                   >
//                     <span>🇵🇹</span> Portugal
//                   </Link>
//                   <Link
//                     to="/permanent-residency/australia-care-workforce"
//                     className="flex items-center justify-between gap-3 px-10 py-2 hover:bg-gray-100 transition text-sm"
//                     onClick={() => setIsOpen(false)}
//                   >
//                     <span>🇦🇺 AU Care Workforce</span>
//                     <span className="inline-flex items-center rounded-full bg-[#EAF0FF] text-[#2542D4] text-[10px] font-medium px-2 py-1">
//                       New
//                     </span>
//                   </Link>
//                 </div>
//               )}
//             </div>

//             <Link
//               to="/pricing"
//               className="px-6 py-3 hover:bg-gray-50 transition"
//               onClick={() => setIsOpen(false)}
//             >
//               Pricing
//             </Link>
//             <Link
//               to="/about"
//               className="px-6 py-3 hover:bg-gray-50 transition"
//               onClick={() => setIsOpen(false)}
//             >
//               About Us
//             </Link>
//             <Link
//               to="/contact"
//               className="px-6 py-3 hover:bg-gray-50 transition"
//               onClick={() => setIsOpen(false)}
//             >
//               Contact
//             </Link>
//             <div className="px-6 py-3 border-t border-gray-200 mt-2">
//               <a
//                 href="https://app.notapaths.com"
//                 target="_blank"
//                 rel="noopener noreferrer"
//                 className="w-full bg-[#202356] text-white px-6 py-2 rounded-lg hover:bg-[#2a2f7a] transition inline-block text-center"
//               >
//                 Login
//               </a>
//             </div>
//           </nav>
//         </div>
//       )}
//     </header>
//   );
// };

// export default Header;
